import { Controller } from "@hotwired/stimulus"
// esbuild's `file` loader (--loader:.wasm=file) fingerprints this binary
// and copies it into app/assets/builds, returning the public URL string
// (/assets/dotlottie-player-<hash>.digested.wasm). Same-origin → covered
// by `connect-src 'self'`, no third-party CDN fetch at runtime.
import wasmUrl from "@lottiefiles/dotlottie-web/dotlottie-player.wasm"
// Shared admission pool (#409): caps how many WASM players animate at once.
// Excess on-screen cosmetics stay on their static poster until a slot frees.
import { dotLottiePool } from "./dotlottie_pool"

// dotLottie player controller — backs CosmeticRendererComponent (#354, #381).
//
// Replaces the previous lottie-web based `lottie-player` controller. The
// LottieFiles `@lottiefiles/dotlottie-web` runtime renders to a <canvas>
// (not <svg>) and reads BOTH `.lottie` (dotLottie ZIP container) and raw
// `.json` Lottie. That dual-format support is the whole point of #381:
// admins will upload `.lottie` directly (next issue), while the ~20
// existing JSON frames keep rendering with zero re-upload (backward
// compat).
//
// Lazy-load: the WASM runtime + glue is dynamically imported only when an
// element carrying this controller FIRST enters the viewport, so feed /
// gallery surfaces without a visible cosmetic pay nothing — not on first
// paint, and not while the avatar sits off-screen.
//
// Viewport lifecycle (#407, epic #412): the previous version instantiated
// the WASM player for every connected element and animated them all at
// once, including the ones scrolled off-screen. In a dense feed that is
// pure waste — CPU, GPU, battery (mobile especially). Architecture
// decision (Luiz): animate ONLY what is on screen. Off-screen the slot
// shows the static poster (#406, painted by the wrapper as a CSS
// background) at zero WASM cost.
//
//   * IntersectionObserver gates activation: the live player is created
//     the first time the element intersects the viewport, and torn down
//     (player.destroy() + canvas removed) when it leaves. The poster
//     background sits permanently underneath, so leaving simply reveals
//     it again — no flicker, no empty slot, nothing to repaint.
//   * visibilitychange pauses the live player when the tab is backgrounded
//     and resumes it when the tab is foregrounded again (only if the
//     element is still on screen). A hidden tab should burn no animation
//     frames.
//
// WASM hosting (CSP law): dotlottie-web defaults to fetching its
// `dotlottie-player.wasm` from cdn.jsdelivr.net. We override that with
// `DotLottie.setWasmUrl(...)` pointing at our OWN fingerprinted asset
// (bundled by esbuild via the `file` loader → served from /assets, same
// origin). This keeps the CSP tight (`connect-src 'self'` already covers
// it — no third-party CDN, no supply-chain fetch at runtime) and avoids a
// silent block when the browser refuses an off-origin WASM fetch.
//
// Accessibility: respects `prefers-reduced-motion: reduce`. With motion
// off we still create the player when it scrolls into view (so the
// cosmetic occupies its slot as the rendered first frame, sharper than
// the poster) but disable autoplay / loop and freeze on frame 0. The
// poster already covers the off-screen / not-yet-loaded case.
//
// Robustness: any failure during import / fetch / parse / init degrades to
// "render nothing" — the wrapper keeps showing its poster background. A
// broken animation must never break the page around it.
export default class extends Controller {
  static values = {
    src: String,
    loop: { type: Boolean, default: true },
    speed: { type: Number, default: 1.0 },
    // Static poster (#406): same-origin URL of the frame-at-rest image
    // the wrapper already paints as a CSS background. It is the inactive
    // state — what shows before the player connects, while off-screen,
    // and after the player is torn down on leave. The lifecycle below
    // never has to touch it; tearing down the canvas just re-reveals it.
    poster: String,
    // Pool opt-out (#409). The shared player cap is right for a FEED (many
    // avatars, only a few need to animate). It is WRONG for a GALLERY like
    // the inventory, where the per-frame art IS the content: with the cap,
    // the excess frames fall back to the GENERIC per-tier poster placeholder
    // and become indistinguishable colored rings. Gallery callers set
    // `pooled-value="false"` so every on-screen frame animates (the
    // IntersectionObserver still gates to what's visible).
    pooled: { type: Boolean, default: true }
  }

  // Margin around the viewport at which a player is considered "near
  // enough" to warm up. Pre-loading one viewport ahead hides the WASM
  // import + first-frame latency behind the scroll, so the animation is
  // already running by the time it is actually visible.
  static ROOT_MARGIN = "200px"

  connect() {
    if (!this.srcValue) return

    this.active = false
    this.loading = false

    // Remember the poster background the component painted inline
    // (CosmeticRendererComponent#lottie_root_style). The poster is a
    // placeholder for the inactive state (loading / off-screen). A frame
    // asset is a RING — its canvas is transparent in the centre AND
    // outside the ring — so a poster ring sitting underneath the live
    // canvas leaks around the real animation as a second, generic-colored
    // border (the "double border" regression). We hide the poster the
    // moment the live canvas actually paints, and restore it on teardown
    // so an off-screen slot falls back to the placeholder again.
    this.posterBackground = this.element.style.backgroundImage || ""

    this.#observe()

    // Pause/resume on tab visibility. A backgrounded tab should not burn
    // animation frames; foregrounding resumes only if still on screen.
    this.onVisibilityChange = this.#handleVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.onVisibilityChange)
  }

  disconnect() {
    if (this.observer) {
      try { this.observer.disconnect() } catch (_) { /* noop */ }
      this.observer = null
    }
    if (this.onVisibilityChange) {
      document.removeEventListener("visibilitychange", this.onVisibilityChange)
      this.onVisibilityChange = null
    }
    this.#teardownPlayer()
    this.active = false
  }

  // Start watching the element. IntersectionObserver is the gate: we only
  // ever build the WASM player while the element is on (or near) screen.
  // If IO is unavailable (very old browser / jsdom), degrade to the old
  // behaviour: activate immediately so the cosmetic still animates.
  #observe() {
    if (typeof IntersectionObserver === "undefined") {
      this.#activate()
      return
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#activate()
          } else {
            this.#deactivate()
          }
        }
      },
      { rootMargin: this.constructor.ROOT_MARGIN, threshold: 0 }
    )
    this.observer.observe(this.element)
  }

  // Bring the slot to life: build the player on first intersection, or
  // resume a paused one on re-entry. A tab that is hidden when the
  // element scrolls into view stays paused — visibilitychange resumes it.
  #activate() {
    this.active = true
    if (document.hidden) return
    this.#requestPlayback()
  }

  // Start (or resume) playback, acquiring a pool slot first (#409). If a player
  // already exists we keep our slot and just resume. Otherwise we ask the pool:
  // granted → build the player; full → stay on the poster and wait for
  // `onPoolAdmit()`. Called from #activate, the pool promotion, and tab
  // re-foreground.
  #requestPlayback() {
    if (!this.stillWantsSlot()) return
    if (this.player) {
      this.#play()
      return
    }
    // Gallery surfaces opt out of the global cap (#409): bypass the pool and
    // load directly — the IntersectionObserver already limits this to the
    // frames actually on screen.
    if (!this.pooledValue) {
      this.#load()
      return
    }
    if (dotLottiePool.acquire(this)) {
      this.#load()
    }
    // else: queued — onPoolAdmit() will fire when a slot frees.
  }

  // Pool callback: a slot freed and we were next in the FIFO queue. We already
  // hold the slot here (the pool added us before calling). Build/resume, or
  // hand the slot back if we no longer want it (scrolled off while queued).
  onPoolAdmit() {
    if (!this.stillWantsSlot()) {
      dotLottiePool.release(this)
      return
    }
    if (this.player) {
      this.#play()
    } else {
      this.#load()
    }
  }

  // Whether this slot is still wanted: element on-screen (this.active), the
  // tab foregrounded, and the element still in the DOM. The pool checks this
  // before promoting so a member that scrolled off while queued is skipped.
  stillWantsSlot() {
    return !!this.active && !document.hidden && this.element.isConnected
  }

  // Element left the viewport: tear the live player down completely and
  // fall back to the poster background that has been underneath all along.
  // Destroying (vs merely pausing) frees the WASM instance + canvas so a
  // long feed never holds N live players in memory at once.
  #deactivate() {
    this.active = false
    this.#teardownPlayer()
  }

  async #load() {
    if (this.loading || this.player) return
    this.loading = true

    try {
      const mod = await import("@lottiefiles/dotlottie-web")
      const DotLottie = mod.DotLottie
      if (!DotLottie) return
      // The element may have disconnected or scrolled away while the
      // dynamic import was in flight. Bail rather than building a player
      // nobody can see — and hand the pool slot back so a waiting one gets it.
      if (!this.element.isConnected || !this.active || document.hidden) {
        dotLottiePool.release(this)
        return
      }

      // Self-host the WASM (see header). Idempotent: setting the same URL
      // twice is a no-op inside the runtime. The fingerprinted URL comes
      // from the build-time `file` import — see #wasmUrl().
      const selfHostedWasm = this.#wasmUrl()
      if (selfHostedWasm) {
        try { DotLottie.setWasmUrl(selfHostedWasm) } catch (_) { /* fall back to default */ }
      }

      const prefersReducedMotion = this.#prefersReducedMotion()

      // dotlottie-web renders into a <canvas>. The component wrapper is a
      // sized box painting the poster as its background; we create a
      // canvas that fills it and sits ON TOP. When this canvas is removed
      // (on leave / disconnect) the poster background shows through again.
      // autoResize keeps the backing store in sync with the (CSS-driven)
      // box dimensions and devicePixelRatio for crisp rendering.
      const canvas = document.createElement("canvas")
      canvas.style.width = "100%"
      canvas.style.height = "100%"
      canvas.setAttribute("aria-hidden", "true")
      this.element.appendChild(canvas)
      this.canvas = canvas

      this.player = new DotLottie({
        canvas,
        src: this.srcValue,
        loop: prefersReducedMotion ? false : this.loopValue,
        autoplay: !prefersReducedMotion,
        speed: (this.speedValue && this.speedValue > 0) ? this.speedValue : 1.0,
        renderConfig: { autoResize: true, freezeOnOffscreen: true }
      })

      // Hide the poster as soon as the live canvas paints, so the generic
      // poster ring never shows through the transparent parts of the real
      // frame (the double-border regression). Both events are belt-and-
      // suspenders: `load` fires when the animation data is ready, `frame`
      // on the first painted frame (also covers the reduced-motion
      // setFrame(0) path below). #hidePoster is idempotent.
      const hidePoster = () => this.#hidePoster()
      try {
        this.player.addEventListener("load", hidePoster)
        this.player.addEventListener("frame", hidePoster)
      } catch (_) { /* older runtime without addEventListener: poster stays */ }

      if (prefersReducedMotion) {
        // Paint frame 0 once the animation is ready, then leave it
        // paused — a static visual without continuous motion.
        this.player.addEventListener("ready", () => {
          try { this.player.setFrame(0) } catch (_) { /* noop */ }
          try { this.player.pause() } catch (_) { /* noop */ }
          this.#hidePoster()
        })
      }
    } catch (_err) {
      // Defensive fallback: leave the poster background showing rather
      // than logging a noisy console error on every cosmetic that fails.
      this.#teardownPlayer()
    } finally {
      this.loading = false
    }
  }

  // Resume a player that was paused (tab re-foregrounded, or element
  // re-entered the viewport while a player instance still existed).
  // Reduced-motion players intentionally stay frozen on frame 0.
  #play() {
    if (!this.player) return
    if (this.#prefersReducedMotion()) return
    try { this.player.play() } catch (_) { /* noop */ }
  }

  // Destroy the live player and remove its canvas. Idempotent. After this
  // the wrapper shows its poster CSS background again — so we restore the
  // poster we hid when the canvas started painting.
  #teardownPlayer() {
    if (this.player) {
      try { this.player.destroy() } catch (_) { /* noop */ }
      this.player = null
    }
    if (this.canvas && this.canvas.parentNode === this.element) {
      try { this.element.removeChild(this.canvas) } catch (_) { /* noop */ }
    }
    this.canvas = null
    this.#restorePoster()
    // Hand the pool slot back (#409) so the next queued cosmetic can animate.
    // Idempotent: a no-op if we never held / already released a slot.
    dotLottiePool.release(this)
  }

  // Hide / restore the inline poster background. Hiding while the live
  // canvas paints prevents the generic poster ring from leaking around
  // the transparent parts of a frame; restoring on teardown brings back
  // the placeholder for the off-screen / reduced state. No-op when the
  // component never painted a poster (e.g. raster/text formats).
  #hidePoster() {
    if (this.posterBackground) this.element.style.backgroundImage = "none"
  }

  #restorePoster() {
    if (this.posterBackground) this.element.style.backgroundImage = this.posterBackground
  }

  // Tab background/foreground. Hidden → pause to stop burning frames.
  // Visible again → resume only if the element is still on screen
  // (this.active), or kick off a load that was deferred because the tab
  // was hidden when the element scrolled in.
  #handleVisibilityChange() {
    if (document.hidden) {
      if (this.player) {
        try { this.player.pause() } catch (_) { /* noop */ }
      }
      return
    }
    if (!this.active) return
    // Re-foregrounded while on-screen: resume the paused player, or (if it was
    // never built / was torn down) re-request a pool slot and load.
    this.#requestPlayback()
  }

  #prefersReducedMotion() {
    return !!(window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches)
  }

  // The fingerprinted same-origin URL of our self-hosted WASM, resolved
  // at build time by esbuild's `file` loader. A string like
  // "/assets/dotlottie-player-XXXX.digested.wasm". If for any reason the
  // import resolves empty, the runtime falls back to its bundled default
  // CDN (still functional, just not our preferred origin).
  #wasmUrl() {
    return wasmUrl || null
  }
}
